/**
 * wpApi — client-side wrapper for the getWordpressData server handler.
 *
 * Every call goes through  GET /api/wordpress?endpoint=<endpoint>&...params
 * which is handled server-side by `getWordpressData()`.
 *
 * Usage:
 *   import wpApi from '@/services/wpApi';
 *
 *   // Simple GET
 *   const { data } = await wpApi.get('navigation/footer-menu', { region: 'en-us' });
 *
 *   // With extra query params
 *   const { data } = await wpApi.get('get-page-by-slug', { region: 'en-us', slug: 'my-slug' });
 */

const PROXY = `${process.env.VUE_APP_GPSPOD_ENDPOINT}/gps/home/get-wordpress-data/`;

/**
 * Core GET — maps directly to the getWordpressData handler signature.
 *
 * @param {string} endpoint  - WP endpoint key, e.g. "navigation/top-navigation"
 * @param {object} params    - Query params forwarded by getWordpressData:
 *                             region, locale, slug, cat, … (anything not "endpoint")
 * @returns {Promise}        - Resolved axios response
 */
function get(endpoint, params = {}) {
  return window.axios.get(PROXY, {
    params: { endpoint, ...params },
  });
}

const wpApi = {
  get,

  // ── Navigation ────────────────────────────────────────────────────────────

  getTopNavigation(region) {
    return get('navigation/top-navigation', { region });
  },

  getTabHeader(region) {
    return get('navigation/tab-header', { region });
  },

  getFooterMenu(region) {
    return get('navigation/footer-menu', { region });
  },

  getFooterUsefulLinks(region) {
    return get('navigation/footer-useful-links', { region });
  },

  getFooterSocialLinks(region) {
    return get('navigation/footer-social-links', { region });
  },

  /** Header navigation — pass the site segment as `region` when needed */
  getHeaderNavigation(region) {
    return get('navigation/header-navigation', { region });
  },

  getHeaderPromos(region) {
    return get('navigation/header-promos', { region });
  },

  getHealthcareProfessionalsHeader() {
    return get('navigation/healthcare-professionals-header');
  },

  getResmedCorporateHeader() {
    return get('navigation/resmed-corporate');
  },

  // ── Content ───────────────────────────────────────────────────────────────

  getPageBySlug(slug, region) {
    return get('get-page-by-slug', { slug, ...(region ? { region } : {}) });
  },

  getPageBySlugWithLocale(slug, locale) {
    return get('get-page-by-slug', { slug, locale });
  },

  getArticleBySlug(region, slug) {
    return get('get-article-by-slug', { locale: region, slug: slug || 'index' });
  },

  listAllArticles(region) {
    return get('list-all-articles', { region, locale: region });
  },

  // ── Products ──────────────────────────────────────────────────────────────

  getProductByCategory(locale, cat, region) {
    return get('get-product-by-category', { locale, cat, region });
  },

  getProductList(site, category) {
    // site segment lives in the URL path — forwarded via region
    return get(`product-list/${category}`, { region: site });
  },

  getProductManuals(params) {
    return get('product-manuals', params);
  },

  // ── Search ────────────────────────────────────────────────────────────────

  multiSiteSearch(query) {
    return get('multi-site-search', { query });
  },

  // ── Brightcove ────────────────────────────────────────────────────────────

  getBrightcoveThumbnail(videoId) {
    return get(`brightcove-thumbnail/${videoId}`);
  },

  // ── Mask selector quiz ────────────────────────────────────────────────────

  getMaskSelectorQuizResults(mappedStr) {
    return get(`mask-selector-quiz/${mappedStr}`);
  },

  /**
   * POST — mask selector quiz form submission (multipart/form-data).
   * The getWordpressData handler only handles GET; POST goes direct to WP.
   */
  submitMaskSelectorQuizForm(formData) {
    return window.axios({
      method: 'post',
      url: `//${location.hostname}/wp-json/resmed/v1/mask-selector-quiz/`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // ── Dealer locator ────────────────────────────────────────────────────────

  getOnlineDealerLocator() {
    return get('online-dealer-locator/');
  },

  // ── Ratings ───────────────────────────────────────────────────────────────

  getRatings(rateableIds, rateableType) {
    return get('ratings', { rateable_ids: rateableIds, rateable_type: rateableType });
  },

  /** POST — goes direct to WP (same-site); getWordpressData handles only GET */
  postRating(rateableId, rateableType) {
    return window.axios.post(`//${location.hostname}/wp-json/resmed/v1/ratings/rate`, {
      rateable_id: rateableId,
      rateable_type: rateableType,
    });
  },

  // ── Get listed ────────────────────────────────────────────────────────────

  /** POST — goes direct to WP (same-site); getWordpressData handles only GET */
  postGetListed(formData) {
    return window.axios({
      method: 'post',
      url: '/wp-json/resmed/v1/get-listed',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default wpApi;
